import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import FormRow from '../../../app/components/FormRow';
import { UploadImageFile } from '../../../common/components/File/UploadImageFile';
import ConfirmationButton from '../../../app/components/ConfirmationButton';

interface EmployeeSignatureEditorProps {
  employeeName: string;
  attachmentId?: string;
  employeeSign?: Buffer;
  signFilename?: string;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    employeeSign?: Buffer,
    attachmentId?: string,
    name?: string | undefined,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  onDeleted: (attachmentId: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const EmployeeSignatureEditor: React.FC<
  EmployeeSignatureEditorProps
> = props => {
  const [sign, setSign] = useState<Buffer>();
  const [name, setName] = useState<string>();
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setSign(props.employeeSign);
      setName(props.signFilename);
    }
  }, [props.signFilename, props.employeeSign, props.show]);

  const createEmployeeSign = async () => {
    setIsLoading(true);
    props
      .onCompleted(sign, props.attachmentId, name)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.attachmentId
              ? `${t(tk.administration.signEdit, { employeeName: props.employeeName })}`
              : t(tk.administration.signAdd, {
                  employeeName: props.employeeName,
                })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormRow
            controlId="logo"
            label={t(tk.administration.employeeSign)}
            className="mt-3"
          >
            <UploadImageFile
              file={sign ? Buffer.from(sign).toString() : ''}
              setFile={fl => setSign(Buffer.from(fl))}
              setName={setName}
              imageHeight={80}
              buttonText={t(tk.administration.uploadGraphicFile)}
              requiredTransparent={true}
            />
          </FormRow>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.attachmentId && (
            <LastEditorInfo
              id={props.attachmentId}
              method={pok.attachments.getLastEditor}
            />
          )}
          {props.attachmentId && (
            <ConfirmationButton
              variant="outline-danger"
              onOK={() => props.onDeleted(props.attachmentId || '')}
              confirmation={t(tk.administration.askDeleteSignEmployee)}
            >
              {t(tk.button.delete)}
            </ConfirmationButton>
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          <Button
            variant="primary"
            onClick={createEmployeeSign}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
